// PagosTabla.jsx
import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
    Card, CardHeader, CardBody, CardTitle,
    Button, ButtonGroup, Row, Col, Form, FormGroup, Label, Input,
    Badge, InputGroup, InputGroupAddon, InputGroupText,
    UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem,
    Collapse, Nav, NavItem, NavLink, TabContent, TabPane
} from "reactstrap";
import { Toaster, toast } from "react-hot-toast";
// Removed ReactTable - using cards instead
import { getPaymentsByMonth, getPaymentsByDate, getAllPayments, getPaymentsWithFilters, renewExpiredMembership } from "./service/paymentService";
import EditPaymentModal from "./modal/EditPaymentModal";
import PaymentAuditModal from "./modal/PaymentAuditModal";
import RenewMembershipModal from "./modal/RenewMembershipModal";
import { useAuth } from "context/AuthContext";
import { useSede } from "context/SedeContext";
import useSedeRefresh from "../../../hooks/useSedeRefresh";

const monthNames = [
    "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
    "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
];

import ReactLoading from "react-loading";
import { overlayStyles, loaderContainerStyles } from "styles/commonStyles";
import { useNotifications } from "hooks/useNotifications";

export default function PagosTabla() {
    const { user } = useAuth();
    const { addSedeParams, addSedeHeaders } = useSede();
    const [pagos, setPagos] = useState([]);
    const [allPayments, setAllPayments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [auditModalOpen, setAuditModalOpen] = useState(false);
    const [renewModalOpen, setRenewModalOpen] = useState(false);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [isLoadedSuccess, setIsLoadedSuccess] = useState(false);
    const [error, setError] = useState(null);
    const [hasAttemptedLoad, setHasAttemptedLoad] = useState(false);
    const [mostrarAuditoria, setMostrarAuditoria] = useState(false);

    // Removed date-based filtering - now showing all payments ordered by expiration status

    // Additional filters
    const [searchTerm, setSearchTerm] = useState("");
    const [paymentMethodFilter, setPaymentMethodFilter] = useState("");
    const [selectedPayments, setSelectedPayments] = useState([]);
    
    // Pagination for cards
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(12);

    // Fetch all payments - ordered by expiration status (expired first, then active)
    const fetchAllPayments = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            setHasAttemptedLoad(true);

            let filters = {};

            // Add search filter if exists
            if (searchTerm) {
                filters.search = searchTerm;
            }
            if (paymentMethodFilter) {
                filters.payment_method = paymentMethodFilter;
            }

            // Add sede parameters
            const sedeParams = addSedeParams(filters);
            const sedeHeaders = addSedeHeaders({});

            // Get all payments (backend will order by expiration status)
            const data = await getPaymentsWithFilters(sedeParams, sedeHeaders);
            
            // Additional sorting on frontend to ensure expired first
            const sortedData = data.sort((a, b) => {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                
                const aValidUntil = a.valid_until ? new Date(String(a.valid_until).split(' ')[0]) : null;
                const bValidUntil = b.valid_until ? new Date(String(b.valid_until).split(' ')[0]) : null;
                
                const aExpired = aValidUntil && aValidUntil < today;
                const bExpired = bValidUntil && bValidUntil < today;
                
                // Primero los vencidos
                if (aExpired && !bExpired) return -1;
                if (!aExpired && bExpired) return 1;
                
                // Si ambos tienen el mismo estado, ordenar por fecha de pago más reciente
                const aDate = new Date(a.date_paid || 0);
                const bDate = new Date(b.date_paid || 0);
                return bDate - aDate;
            });
            
            setPagos(sortedData);
            setIsLoadedSuccess(true);

        } catch (err) {
            setError(err);
            console.error(err);
        } finally {
            setLoading(false);
        }

    }, [searchTerm, paymentMethodFilter, addSedeParams, addSedeHeaders]);



    // Use pagos directly since filtering is now done on backend
    const filteredPagos = pagos || [];
    
    // Pagination calculations
    const totalPages = Math.ceil(filteredPagos.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedPagos = filteredPagos.slice(startIndex, endIndex);
    
    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [filteredPagos.length, searchTerm, paymentMethodFilter]);

    // Calculate summary statistics
    const summaryStats = useMemo(() => {
        const total = filteredPagos.reduce((sum, p) => {
            const amount = parseFloat(p.amount) || 0;
            return sum + amount;
        }, 0);
        const count = filteredPagos.length;
        const avgAmount = count > 0 ? total / count : 0;

        const methodCounts = filteredPagos.reduce((acc, p) => {
            const method = p.payment_method || 'No especificado';
            acc[method] = (acc[method] || 0) + 1;
            return acc;
        }, {});

        return {
            total: Number(total),
            count: Number(count),
            avgAmount: Number(avgAmount),
            methodCounts
        };
    }, [filteredPagos]);

    useEffect(() => {
        // Cargar todos los pagos al montar el componente
        fetchAllPayments();
    }, []);

    // Trigger filtering when additional filters change
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            fetchAllPayments();
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [searchTerm, paymentMethodFilter, fetchAllPayments]);

    // Auto-refresh when sede changes
    useSedeRefresh(fetchAllPayments, [addSedeParams, addSedeHeaders]);

    const handleEditar = (row) => {
        setSelectedPayment(row);
        setEditModalOpen(true);
    };

    const handleAudit = (row) => {
        setSelectedPayment(row);
        setAuditModalOpen(true);
    };

    const handleRenewExpired = (payment) => {
        setSelectedPayment(payment);
        setRenewModalOpen(true);
    };

    const handleRenewed = () => {
        fetchAllPayments();
    };

    // Role-based access control
    const canEditPayments = () => {
        // Verificar tanto user.role (singular) como user.roles (array) para compatibilidad
        const userRoles = user?.roles || [];
        const userRole = user?.role;
        const isAdmin = user?.is_superuser || userRoles.includes('admin') || userRole === 'admin';
        const isSecretaria = userRoles.includes('secretaria') || userRole === 'secretaria';
        const hasPermission = isAdmin || isSecretaria;
        
        if (!hasPermission) {
            console.log('Usuario sin permisos de edición:', { 
                role: userRole, 
                roles: userRoles, 
                is_superuser: user?.is_superuser,
                user 
            });
        }
        return hasPermission;
    };

    const canViewAudit = () => {
        const userRoles = user?.roles || [];
        const userRole = user?.role;
        const isAdmin = user?.is_superuser || userRoles.includes('admin') || userRole === 'admin';
        const isSecretaria = userRoles.includes('secretaria') || userRole === 'secretaria';
        return isAdmin || isSecretaria;
    };

    const canDeletePayments = () => {
        const userRoles = user?.roles || [];
        const userRole = user?.role;
        return user?.is_superuser || userRoles.includes('admin') || userRole === 'admin';
    };

    const columns = [
        {
            Header: "Seleccionar",
            accessor: "select",
            disableSortBy: true,
            width: 50,
            Cell: ({ row }) => (
                <Input
                    type="checkbox"
                    checked={selectedPayments.includes(row.original.id)}
                    onChange={(e) => {
                        if (e.target.checked) {
                            setSelectedPayments([...selectedPayments, row.original.id]);
                        } else {
                            setSelectedPayments(selectedPayments.filter(id => id !== row.original.id));
                        }
                    }}
                />
            ),
        },
        {
            Header: "ID",
            accessor: "id",
            width: 60
        },
        {
            Header: "Cliente",
            accessor: (row) => `${row.client || ''}`,
            id: "cliente",
            Cell: ({ value }) => (
                <div>
                    <strong>{value || "—"}</strong>
                </div>
            ),
        },
        {
            Header: "Plan",
            accessor: "membership.name",
            Cell: ({ value }) => (
                <Badge color="info" pill>
                    {value || "—"}
                </Badge>
            ),
        },
        {
            Header: "Monto (Q)",
            accessor: "amount",
            Cell: ({ value }) => {
                const numValue = parseFloat(value) || 0;
                return (
                    <strong style={{ color: "#28a745" }}>
                        Q{numValue.toFixed(2)}
                    </strong>
                );
            },
        },
        {
            Header: "Método",
            accessor: "payment_method",
            Cell: ({ value }) => {
                const colors = {
                    'Efectivo': 'success',
                    'Transferencia': 'primary',
                    'VisaLink': 'warning'
                };
                return (
                    <Badge color={colors[value] || 'secondary'}>
                        {value || "—"}
                    </Badge>
                );
            },
        },
        {
            Header: "Fecha de Pago",
            accessor: (row) => row.date_paid?.split("T")[0] || "—",
            id: "date_paid",
        },
        {
            Header: "Recibo",
            accessor: (row) => row.receipt_number || "—",
            id: "receipt_number",
        },
        {
            Header: "Período",
            accessor: (row) => row.month_year || "—",
            id: "month_year",
        },
        {
            Header: "Válido Desde",
            accessor: (row) => row.valid_from?.split("T")[0] || "—",
            id: "valid_from",
        },
        {
            Header: "Válido Hasta",
            accessor: (row) => row.valid_until || "—",
            id: "valid_until",
            Cell: ({ value, row }) => {
                if (!value || value === "—") return "—";
                try {
                    // Limpiar la fecha (remover cualquier carácter extra)
                    const cleanDate = String(value).trim().split(' ')[0];
                    const validDate = new Date(cleanDate);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    validDate.setHours(0, 0, 0, 0);
                    
                    if (isNaN(validDate.getTime())) {
                        return <span>{value}</span>;
                    }
                    
                    const isExpired = validDate < today;
                    return (
                        <span style={{ color: isExpired ? '#dc3545' : '#28a745', fontWeight: isExpired ? 'bold' : 'normal' }}>
                            {cleanDate}
                            {isExpired && <i className="fa fa-exclamation-triangle ml-1" />}
                        </span>
                    );
                } catch (error) {
                    return <span>{value}</span>;
                }
            },
        },
        ...(mostrarAuditoria
            ? [
                {
                    Header: "Creado por",
                    accessor: (row) => row.created_by || "admin",
                    id: "created_by",
                },
                {
                    Header: "Fecha de creación",
                    accessor: (row) =>
                        row.created_at?.split("T")[0] ||
                        row.date_paid?.split("T")[0] ||
                        "—",
                    id: "created_at",
                },
                {
                    Header: "Modificado por",
                    accessor: (row) => row.modified_by || "—",
                    id: "modified_by",
                },
                {
                    Header: "Fecha modif",
                    accessor: (row) =>
                        row.updated_at && row.updated_at !== row.created_at
                            ? row.updated_at.split("T")[0]
                            : "—",
                    id: "updated_at",
                },
            ]
            : []),
        {
            Header: "Acciones",
            accessor: "actions",
            disableSortBy: true,
            width: 250,
            minWidth: 200,
            Cell: ({ row }) => {
                const payment = row.original;
                const validUntil = payment.valid_until;
                let isExpired = false;
                
                if (validUntil && validUntil !== "—") {
                    try {
                        // Limpiar la fecha (remover cualquier carácter extra como "A" al final)
                        const cleanDate = String(validUntil).trim().split(' ')[0];
                        const validDate = new Date(cleanDate);
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        validDate.setHours(0, 0, 0, 0);
                        
                        // Verificar que la fecha sea válida
                        if (!isNaN(validDate.getTime())) {
                            isExpired = validDate < today;
                        }
                    } catch (error) {
                        console.error('Error al parsear fecha de vencimiento:', error, validUntil);
                    }
                }

                return (
                    <div className="d-flex gap-1 flex-wrap">
                    {canViewAudit() && (
                        <Button
                            color="info"
                            size="sm"
                            className="btn-icon btn-link"
                                onClick={() => handleAudit(payment)}
                            title="Ver auditoría"
                        >
                            <i className="fa fa-history" />
                        </Button>
                    )}
                    {canEditPayments() && (
                            <>
                        <Button
                            color="warning"
                            size="sm"
                            className="btn-icon btn-link"
                                    onClick={() => handleEditar(payment)}
                            title="Editar pago"
                        >
                            <i className="fa fa-edit" />
                                </Button>
                                <Button
                                    color="info"
                                    size="sm"
                                    className="btn-icon btn-link"
                                    onClick={() => handleRenewExpired(payment)}
                                    title="Renovar membresía"
                                >
                                    <i className="fa fa-sync-alt" />
                                </Button>
                            </>
                    )}
                    {canDeletePayments() && (
                        <Button
                            color="danger"
                            size="sm"
                            className="btn-icon btn-link"
                            onClick={() => {
                                if (window.confirm('¿Estás seguro de eliminar este pago?')) {
                                    // TODO: Implement delete functionality
                                    toast.error('Función de eliminación no implementada');
                                }
                            }}
                            title="Eliminar pago"
                        >
                            <i className="fa fa-trash" />
                        </Button>
                    )}
                </div>
                );
            },
        },
    ];

    const formatMes = (isoStr) => {
        const [year, month] = isoStr.split("-");
        return `${monthNames[parseInt(month) - 1]} ${year}`;
    };

    useNotifications(isLoadedSuccess, hasAttemptedLoad ? error : null, {
        success: 'Pagos cargados exitosamente.',
        error: (e) => e.message || 'Error al cargar pagos.',
    });

    return (
        <>
            {loading && (
                <div style={overlayStyles}>
                    <div style={loaderContainerStyles}>
                        <ReactLoading type="spin" color="white" height="40%" />
                    </div>
                </div>
            )}
            <Toaster position="top-right" />
            <div className="content">
                <Card>
                    <CardHeader>
                        <CardTitle tag="h4">
                            <i className="nc-icon nc-money-coins mr-2"></i>
                            Historial de Pagos
                            <small className="text-muted ml-2">
                                {(() => {
                                    const userRoles = user?.roles || [];
                                    const userRole = user?.role;
                                    if (user?.is_superuser || userRoles.includes('admin') || userRole === 'admin') {
                                        return 'Administrador';
                                    } else if (userRoles.includes('secretaria') || userRole === 'secretaria') {
                                        return 'Secretaría';
                                    } else if (userRoles.includes('coach') || userRole === 'coach') {
                                        return 'Instructor';
                                    }
                                    return 'Usuario';
                                })()}
                            </small>
                        </CardTitle>
                        <div className="d-flex gap-2">
                            <Button
                                color="warning"
                                outline
                                size="sm"
                                onClick={async () => {
                                    const email = prompt("Ingresa el correo para recibir el reporte:");
                                    if (!email) return;
                                    try {
                                        const { sendExpiredMembershipReport } = await import('./service/paymentService');
                                        await sendExpiredMembershipReport(email);
                                        toast.success(`Reporte enviado a ${email}`);
                                    } catch (err) {
                                        toast.error("Error al enviar reporte: " + (err?.response?.data?.detail || err.message));
                                    }
                                }}
                            >
                                <i className="fa fa-envelope mr-1"></i>
                                Enviar reporte
                            </Button>
                            <Button
                                color="info"
                                outline
                                size="sm"
                                onClick={() => setMostrarAuditoria(!mostrarAuditoria)}
                            >
                                <i className="fa fa-eye mr-1"></i>
                                {mostrarAuditoria ? "Ocultar detalles" : "Mostrar detalles"}
                            </Button>
                            <Button
                                color="success"
                                outline
                                size="sm"
                                onClick={() => {
                                    const csvContent = "data:text/csv;charset=utf-8," +
                                        "ID,Cliente,Plan,Monto,Método,Fecha Pago,Válido Hasta\n" +
                                        filteredPagos.map(p =>
                                            `${p.id},"${p.client}","${p.membership?.name || ''}",${p.amount},"${p.payment_method}","${p.date_paid?.split('T')[0] || ''}","${p.valid_until || ''}"`
                                        ).join("\n");
                                    const encodedUri = encodeURI(csvContent);
                                    const link = document.createElement("a");
                                    link.setAttribute("href", encodedUri);
                                    link.setAttribute("download", `pagos_${new Date().toISOString().split('T')[0]}.csv`);
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                }}
                                disabled={filteredPagos.length === 0}
                            >
                                <i className="fa fa-download mr-1"></i>
                                Exportar CSV
                            </Button>
                        </div>
                    </CardHeader>
                    <CardBody>
                        {/* Summary Cards */}
                        <Row className="mb-4">
                            <Col md="3">
                                <Card className="card-stats">
                                    <CardBody>
                                        <Row>
                                            <Col xs="5">
                                                <div className="icon-big text-center icon-warning">
                                                    <i className="nc-icon nc-money-coins text-warning"></i>
                                                </div>
                                            </Col>
                                            <Col xs="7">
                                                <div className="numbers">
                                                    <p className="card-category">Total</p>
                                                    <CardTitle tag="p">Q{(summaryStats.total || 0).toFixed(2)}</CardTitle>
                                                </div>
                                            </Col>
                                        </Row>
                                    </CardBody>
                                </Card>
                            </Col>
                            <Col md="3">
                                <Card className="card-stats">
                                    <CardBody>
                                        <Row>
                                            <Col xs="5">
                                                <div className="icon-big text-center icon-success">
                                                    <i className="nc-icon nc-single-02 text-success"></i>
                                                </div>
                                            </Col>
                                            <Col xs="7">
                                                <div className="numbers">
                                                    <p className="card-category">Pagos</p>
                                                    <CardTitle tag="p">{summaryStats.count || 0}</CardTitle>
                                                </div>
                                            </Col>
                                        </Row>
                                    </CardBody>
                                </Card>
                            </Col>
                            <Col md="3">
                                <Card className="card-stats">
                                    <CardBody>
                                        <Row>
                                            <Col xs="5">
                                                <div className="icon-big text-center icon-info">
                                                    <i className="nc-icon nc-chart-bar-32 text-info"></i>
                                                </div>
                                            </Col>
                                            <Col xs="7">
                                                <div className="numbers">
                                                    <p className="card-category">Promedio</p>
                                                    <CardTitle tag="p">Q{(summaryStats.avgAmount || 0).toFixed(2)}</CardTitle>
                                                </div>
                                            </Col>
                                        </Row>
                                    </CardBody>
                                </Card>
                            </Col>
                            <Col md="3">
                                <Card className="card-stats">
                                    <CardBody>
                                        <Row>
                                            <Col xs="5">
                                                <div className="icon-big text-center icon-primary">
                                                    <i className="nc-icon nc-credit-card text-primary"></i>
                                                </div>
                                            </Col>
                                            <Col xs="7">
                                                <div className="numbers">
                                                    <p className="card-category">Métodos</p>
                                                    <CardTitle tag="p">{Object.keys(summaryStats.methodCounts || {}).length}</CardTitle>
                                                </div>
                                            </Col>
                                        </Row>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>

                        {/* Search Filters */}
                        <Row className="mb-4">
                            <Col md="8">
                                <InputGroup>
                                    <InputGroupAddon addonType="prepend">
                                        <InputGroupText>
                                            <i className="fa fa-search"></i>
                                        </InputGroupText>
                                    </InputGroupAddon>
                                    <Input
                                        type="text"
                                        placeholder="Buscar por cliente, plan o método de pago..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </InputGroup>
                            </Col>
                            <Col md="4">
                                <div className="d-flex align-items-center h-100 gap-2">
                                    <Input
                                        type="select"
                                        value={paymentMethodFilter}
                                        onChange={(e) => setPaymentMethodFilter(e.target.value)}
                                        style={{ flex: 1 }}
                                    >
                                        <option value="">Todos los métodos</option>
                                        <option value="Efectivo">Efectivo</option>
                                        <option value="Transferencia">Transferencia</option>
                                        <option value="VisaLink">VisaLink</option>
                                    </Input>
                                    <Button
                                        color="secondary"
                                        outline
                                        size="sm"
                                        onClick={() => {
                                            setSearchTerm("");
                                            setPaymentMethodFilter("");
                                        }}
                                    >
                                        <i className="fa fa-times"></i>
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                        
                        {/* Info Badge */}
                        <Row className="mb-3">
                            <Col md="12">
                                <div className="alert alert-info mb-0">
                                    <i className="fa fa-info-circle mr-2"></i>
                                    <strong>Ordenamiento automático:</strong> Los pagos vencidos aparecen primero, seguidos de los pagos activos.
                                    {filteredPagos.length > 0 && (
                                        <span className="ml-2">
                                            <Badge color="info">{filteredPagos.length} pagos encontrados</Badge>
                                        </span>
                                    )}
                                </div>
                            </Col>
                        </Row>

                        {/* Error Display */}
                        {error && (
                            <Row className="mb-3">
                                <Col md="12">
                                    <div className="alert alert-danger">
                                        <i className="fa fa-exclamation-triangle mr-2"></i>
                                        {error.message || error}
                                    </div>
                                </Col>
                            </Row>
                        )}

                        {/* Bulk Actions */}
                        {selectedPayments.length > 0 && (
                            <Row className="mb-3">
                                <Col md="12">
                                    <div className="alert alert-info d-flex justify-content-between align-items-center">
                                        <span>
                                            <i className="fa fa-info-circle mr-2"></i>
                                            {selectedPayments.length} pago(s) seleccionado(s)
                                        </span>
                                        <div className="d-flex gap-2">
                                            <Button
                                                color="success"
                                                size="sm"
                                                onClick={() => {
                                                    // TODO: Implement bulk export
                                                    toast.success('Exportación masiva no implementada');
                                                }}
                                            >
                                                <i className="fa fa-download mr-1"></i>
                                                Exportar Seleccionados
                                            </Button>
                                            <Button
                                                color="secondary"
                                                size="sm"
                                                onClick={() => setSelectedPayments([])}
                                            >
                                                <i className="fa fa-times mr-1"></i>
                                                Deseleccionar
                                            </Button>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        )}

                        {/* Payment Cards View */}
                        {filteredPagos.length > 0 ? (
                            <>
                                <Row className="mb-3">
                                    <Col md="6">
                                        <div className="d-flex align-items-center">
                                            <Label className="mb-0 mr-2">Mostrar:</Label>
                                            <Input
                                                type="select"
                                                value={itemsPerPage}
                                                onChange={(e) => {
                                                    setItemsPerPage(Number(e.target.value));
                                                    setCurrentPage(1);
                                                }}
                                                style={{ width: 'auto' }}
                                            >
                                                <option value={6}>6 por página</option>
                                                <option value={12}>12 por página</option>
                                                <option value={24}>24 por página</option>
                                                <option value={48}>48 por página</option>
                                            </Input>
                                            <span className="ml-3 text-muted">
                                                Mostrando {startIndex + 1}-{Math.min(endIndex, filteredPagos.length)} de {filteredPagos.length} pagos
                                            </span>
                                        </div>
                                    </Col>
                                    <Col md="6" className="text-right">
                                        {totalPages > 1 && (
                                            <div className="d-flex justify-content-end align-items-center">
                                                <Button
                                                    color="secondary"
                                                    size="sm"
                                                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                                    disabled={currentPage === 1}
                                                    className="mr-2"
                                                >
                                                    <i className="fa fa-chevron-left"></i>
                                                </Button>
                                                <span className="mx-2">
                                                    Página {currentPage} de {totalPages}
                                                </span>
                                                <Button
                                                    color="secondary"
                                                    size="sm"
                                                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                                    disabled={currentPage === totalPages}
                                                >
                                                    <i className="fa fa-chevron-right"></i>
                                                </Button>
                                            </div>
                                        )}
                                    </Col>
                                </Row>
                                <Row>
                                {paginatedPagos.map((payment) => {
                                    const validUntil = payment.valid_until;
                                    let isExpired = false;
                                    let validDate = null;
                                    
                                    if (validUntil && validUntil !== "—") {
                                        try {
                                            const cleanDate = String(validUntil).trim().split(' ')[0];
                                            validDate = new Date(cleanDate + 'T00:00:00'); // Asegurar zona horaria
                                            const today = new Date();
                                            today.setHours(0, 0, 0, 0);
                                            validDate.setHours(0, 0, 0, 0);
                                            
                                            if (!isNaN(validDate.getTime())) {
                                                // Comparar solo las fechas (sin hora)
                                                const todayStr = today.toISOString().split('T')[0];
                                                const validStr = validDate.toISOString().split('T')[0];
                                                isExpired = validStr < todayStr;
                                                
                                                // Debug log siempre para ver qué está pasando
                                                console.log('Verificando pago:', {
                                                    paymentId: payment.id,
                                                    client: payment.client,
                                                    validUntil: cleanDate,
                                                    validStr,
                                                    todayStr,
                                                    isExpired,
                                                    comparison: `${validStr} < ${todayStr} = ${isExpired}`
                                                });
                                            } else {
                                                console.warn('Fecha inválida:', cleanDate, 'para pago', payment.id);
                                            }
                                        } catch (error) {
                                            console.error('Error al parsear fecha:', error, validUntil);
                                        }
                                    } else {
                                        console.warn('Pago sin fecha de vencimiento:', payment.id);
                                    }
                                    
                                    // Debug: verificar permisos
                                    const canEdit = canEditPayments();
                                    const canRenew = isExpired && canEdit;
                                    if (isExpired) {
                                        console.log('Permisos para renovar:', {
                                            paymentId: payment.id,
                                            isExpired,
                                            canEdit,
                                            canRenew,
                                            userRole: user?.role,
                                            userRoles: user?.roles,
                                            is_superuser: user?.is_superuser
                                        });
                                    }

                                    const methodColors = {
                                        'Efectivo': 'success',
                                        'Transferencia': 'primary',
                                        'VisaLink': 'warning'
                                    };

                                    return (
                                        <Col md="6" lg="4" key={payment.id} className="mb-4">
                                            <Card className={`h-100 shadow-sm ${isExpired ? 'border-danger' : 'border-success'}`} style={{ borderWidth: '2px' }}>
                                                <CardHeader className={`d-flex justify-content-between align-items-start ${isExpired ? 'bg-danger text-white' : 'bg-light'}`}>
                                                    <div>
                                                        <CardTitle tag="h6" className="mb-1">
                                                            <strong>{payment.client || "—"}</strong>
                                                        </CardTitle>
                                                        <small className="text-muted">ID: {payment.id}</small>
                                                    </div>
                                                    {isExpired && (
                                                        <Badge color="danger" pill>
                                                            <i className="fa fa-exclamation-triangle mr-1"></i>
                                                            Vencido
                                                        </Badge>
                                                    )}
                                                </CardHeader>
                                                <CardBody>
                                                    <div className="mb-3">
                                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                                            <span className="text-muted small">Plan:</span>
                                                            <Badge color="info" pill>
                                                                {payment.membership?.name || "—"}
                                                            </Badge>
                                                        </div>
                                                        
                                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                                            <span className="text-muted small">Monto:</span>
                                                            <strong className="text-success" style={{ fontSize: '1.2rem' }}>
                                                                Q{parseFloat(payment.amount || 0).toFixed(2)}
                                                            </strong>
                                                        </div>
                                                        
                                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                                            <span className="text-muted small">Método:</span>
                                                            <Badge color={methodColors[payment.payment_method] || 'secondary'} pill>
                                                                {payment.payment_method || "—"}
                                                            </Badge>
                                                        </div>
                                                        
                                                        <hr className="my-2" />
                                                        
                                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                                            <span className="text-muted small">Fecha de Pago:</span>
                                                            <span>{payment.date_paid?.split("T")[0] || "—"}</span>
                                                        </div>
                                                        
                                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                                            <span className="text-muted small">Recibo:</span>
                                                            <span className="font-monospace small">{payment.receipt_number || "—"}</span>
                                                        </div>
                                                        
                                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                                            <span className="text-muted small">Período:</span>
                                                            <span>{payment.month_year || "—"}</span>
                                                        </div>
                                                        
                                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                                            <span className="text-muted small">Válido Desde:</span>
                                                            <span>{payment.valid_from?.split("T")[0] || "—"}</span>
                                                        </div>
                                                        
                                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                                            <span className="text-muted small">Válido Hasta:</span>
                                                            <span 
                                                                style={{ 
                                                                    color: isExpired ? '#dc3545' : '#28a745',
                                                                    fontWeight: isExpired ? 'bold' : 'normal'
                                                                }}
                                                            >
                                                                {validUntil ? String(validUntil).trim().split(' ')[0] : "—"}
                                                                {isExpired && <i className="fa fa-exclamation-triangle ml-1" />}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    
                                                    {/* Action Buttons */}
                                                    <div className="d-flex flex-wrap gap-2 justify-content-center border-top pt-3">
                                                        {/* Botón de Renovar - Siempre visible */}
                                                        <Button
                                                            color="info"
                                                            size="md"
                                                            onClick={() => handleRenewExpired(payment)}
                                                            title="Renovar membresía"
                                                            className="font-weight-bold"
                                                            style={{ 
                                                                minWidth: '140px',
                                                                fontWeight: 'bold',
                                                                backgroundColor: '#17a2b8',
                                                                borderColor: '#17a2b8',
                                                                color: '#ffffff'
                                                            }}
                                                        >
                                                            <i className="fa fa-sync-alt mr-2"></i>
                                                            Renovar
                                                        </Button>
                                                        
                                                        {canViewAudit() && (
                                                            <Button
                                                                color="info"
                                                                size="sm"
                                                                onClick={() => handleAudit(payment)}
                                                                title="Ver auditoría"
                                                            >
                                                                <i className="fa fa-history mr-1"></i>
                                                                Auditoría
                                                            </Button>
                                                        )}
                                                        {canEditPayments() && (
                                                            <Button
                                                                color="warning"
                                                                size="sm"
                                                                onClick={() => handleEditar(payment)}
                                                                title="Editar pago"
                                                            >
                                                                <i className="fa fa-edit mr-1"></i>
                                                                Editar
                                                            </Button>
                                                        )}
                                                        {canDeletePayments() && (
                                                            <Button
                                                                color="danger"
                                                                size="sm"
                                                                onClick={() => {
                                                                    if (window.confirm('¿Estás seguro de eliminar este pago?')) {
                                                                        toast.error('Función de eliminación no implementada');
                                                                    }
                                                                }}
                                                                title="Eliminar pago"
                                                            >
                                                                <i className="fa fa-trash mr-1"></i>
                                                                Eliminar
                                                            </Button>
                                                        )}
                                                    </div>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                    );
                                })}
                                </Row>
                                {totalPages > 1 && (
                                    <Row className="mt-4">
                                        <Col md="12" className="text-center">
                                            <div className="d-flex justify-content-center align-items-center">
                                                <Button
                                                    color="secondary"
                                                    size="sm"
                                                    onClick={() => setCurrentPage(1)}
                                                    disabled={currentPage === 1}
                                                    className="mr-2"
                                                >
                                                    <i className="fa fa-angle-double-left"></i>
                                                </Button>
                                                <Button
                                                    color="secondary"
                                                    size="sm"
                                                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                                    disabled={currentPage === 1}
                                                    className="mr-2"
                                                >
                                                    <i className="fa fa-chevron-left"></i> Anterior
                                                </Button>
                                                <span className="mx-3">
                                                    Página {currentPage} de {totalPages}
                                                </span>
                                                <Button
                                                    color="secondary"
                                                    size="sm"
                                                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                                    disabled={currentPage === totalPages}
                                                    className="mr-2"
                                                >
                                                    Siguiente <i className="fa fa-chevron-right"></i>
                                                </Button>
                                                <Button
                                                    color="secondary"
                                                    size="sm"
                                                    onClick={() => setCurrentPage(totalPages)}
                                                    disabled={currentPage === totalPages}
                                                >
                                                    <i className="fa fa-angle-double-right"></i>
                                                </Button>
                                            </div>
                                        </Col>
                                    </Row>
                                )}
                            </>
                        ) : !loading ? (
                            <div className="text-center text-muted p-5 border border-2 rounded">
                                <i className="nc-icon nc-money-coins" style={{ fontSize: '3rem', opacity: 0.3 }}></i>
                                <h5 className="mt-3">No se encontraron pagos</h5>
                                <p className="mb-0">
                                    {searchTerm || paymentMethodFilter 
                                        ? "No se encontraron pagos con los filtros aplicados. Intenta ajustar la búsqueda."
                                        : "No hay pagos registrados en el sistema."
                                    }
                                </p>
                            </div>
                        ) : null}
                    </CardBody>
                </Card>
                <EditPaymentModal
                    isOpen={editModalOpen}
                    toggle={() => setEditModalOpen(false)}
                    payment={selectedPayment}
                    onUpdated={() => fetchPaymentsByDate()}
                />
                <PaymentAuditModal
                    isOpen={auditModalOpen}
                    toggle={() => setAuditModalOpen(false)}
                    payment={selectedPayment}
                />
                <RenewMembershipModal
                    isOpen={renewModalOpen}
                    toggle={() => setRenewModalOpen(false)}
                    payment={selectedPayment}
                    onRenewed={handleRenewed}
                />
            </div>
        </>
    );
}
